import type {
  GetReservationExternalResponse,
  PatchReservationExternalRequest,
  WbeReservationRow,
} from "../api/contracts/reservation";
import { isManualQuantityMode } from "./productAdMode";

type PatchRows = NonNullable<PatchReservationExternalRequest["rows"]>;
type PatchRow = PatchRows[number];

const applyPatchRowToReservationRow = (
  row: WbeReservationRow,
  patchRow: PatchRow,
): WbeReservationRow => {
  // Mirror backend semantics: when `productAdIds` is omitted from the patch,
  // preserve current ad selection. Avoids stale-snapshot clobbering when
  // unrelated PATCHes (mandatory-products sync, sitemap, etc.) interleave
  // with rapid checkbox/stepper clicks.
  // Activity rows carry no categoryAd (and thus no product-ad selection to
  // reconcile) — only the bookableId rule below applies to them.
  if (!row.categoryAd) {
    return {
      ...row,
      bookableId:
        "bookableId" in patchRow ? (patchRow.bookableId ?? undefined) : row.bookableId,
    };
  }

  const productAdIdsPresent = "productAdIds" in patchRow && patchRow.productAdIds !== undefined;
  const nextSelectedProductAdIds = productAdIdsPresent
    ? [...patchRow.productAdIds!]
    : row.categoryAd.selectedProductAdIds;

  const previousQuantities = row.categoryAd.selectedProductAdQuantities ?? {};
  const availableProductAdsById = new Map(
    (row.categoryAd.availableProductAds ?? []).map((ad) => [ad.id, ad]),
  );
  const nextQuantities: Record<string, number> = {};
  for (const productAdId of nextSelectedProductAdIds) {
    if (productAdId in previousQuantities) {
      nextQuantities[productAdId] = previousQuantities[productAdId];
      continue;
    }
    // Newly selected ad: only seed a quantity for manual modes (user-driven).
    // Derived modes (person, night, stay, ...) compute their quantity from row
    // state in getQuantityForDisplay; seeding 1 here would mask that fallback
    // and make the cart breakdown stale until the server GET refresh lands.
    const ad = availableProductAdsById.get(productAdId);
    if (ad && isManualQuantityMode(ad.quantityMode ?? ad.mode)) {
      nextQuantities[productAdId] = 1;
    }
  }

  return {
    ...row,
    // Only touch bookableId when the key is explicitly present in the patch
    // (matches the backend rule: `undefined` = preserve, `null` = clear).
    bookableId:
      "bookableId" in patchRow ? (patchRow.bookableId ?? undefined) : row.bookableId,
    // Same rule for the picked-flag — matches backend `patchRow` semantics.
    // Without an optimistic write here the green pill would only appear once
    // the server GET refetch lands, so the button stays black for a beat
    // after every pick.
    lockedBookable:
      "lockedBookable" in patchRow ? patchRow.lockedBookable : row.lockedBookable,
    categoryAd: {
      ...row.categoryAd,
      selectedProductAdIds: nextSelectedProductAdIds,
      selectedProductAdQuantities: nextQuantities,
    },
  };
};

/**
 * Optimistically apply a reservation PATCH body onto a cached reservation.
 *
 * Only updates what we can reliably derive on the client: each referenced row's
 * `selectedProductAdIds`, `selectedProductAdQuantities` and `bookableId`. Per-row
 * `price`/`products` and the cart totals are intentionally left stale — they are
 * recomputed server-side and replaced as soon as the PATCH response lands.
 *
 * Rows that are NOT referenced by the patch are returned untouched (same ref),
 * so this never optimistically removes a row (e.g. category removal stays
 * server-confirmed, exactly as before).
 */
export const applyOptimisticReservationPatch = (
  reservation: GetReservationExternalResponse,
  patchRows: PatchRows,
): GetReservationExternalResponse => {
  if (patchRows.length === 0) return reservation;
  const patchRowById = new Map(patchRows.map((patchRow) => [patchRow.id, patchRow]));

  return {
    ...reservation,
    rows: reservation.rows.map((row) => {
      const patchRow = patchRowById.get(row.rowId);
      return patchRow ? applyPatchRowToReservationRow(row, patchRow) : row;
    }),
  };
};
